import { Event, Item, Stage, Day, TimeRange } from "./types.ts";

export function transformFilterSets(data: any): any {
  let filterSets:any = []
  const filterCollections = data.filterCollections
  filterCollections?.map((filterCollection:any) => {
    filterCollection.filterGroups?.map((filterGroup:any) => {
      filterSets = filterGroup.filterSets
    })
  })
  return filterSets
}

export function transformItemsToEvents(items: Item[], stages:Stage[]): Event[] {
  return items.map((item) => {
    let title = '';

    if (item.label && item.label.trim() !== '') {
      title = item.label;
    } else if (item.artists && item.artists.length > 0) {
      title = item.artists
        .map((artist) => {
          const nameContent = artist.name;
          const tagClass = artist.nameUppercase ? 'uppercase' : '';

          if (artist.detailPageUrl && artist.detailPageUrl.trim() !== '') {
            return `<span class="${tagClass}"><a href="${artist.detailPageUrl}">${nameContent}</a></span>`;
          } else {
            return `<span class="${tagClass}">${nameContent}</span>`;
          }
        })
        .join('<span class="ec-event-title-divider"> X </span>');
    }

    if (item.subname) {
      title += ` <span class="${item.subnameUppercase ? 'uppercase' : ''}">${item.subname}</span>`
    }

    const stageName = stages.find((stage:any) => stage.id === item.stage)?.title

    return {
      id: item.identifier,
      title,
      start: item.start,
      end: item.end,
      resourceId: item.stage,
      allDay: false,
      extendedProps: {
        stage: stageName
      }
    };
  });
}

export function transformFilterToStages(stages: any[]): Stage[] {
  return stages.map((stage) => {
    return {
      id: stage.identifier,
      title: stage.label,
      enabled: true
    };
  });
}

export function transformFilterToDays(days: any[]): Day[] {
  return days.map((day) => {
    const locale = document.documentElement.lang
    const date = new Date(day.min.replace(' ', 'T')); // → ISO-kompatibel machen
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
    const dayMonth = new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'long'}).format(date);

    return {
      id: day.identifier,
      title: `<span>${weekday}</span>  <span>${dayMonth}</span>`,
      dayMonth: dayMonth,
      weekday: weekday,
      min: day.min,
      max: day.max
    };
  });
}

export function transformFilterSetToStages(filterSets: any): any {
  const stages: Stage[] = [];
  const stageFilterSet = filterSets.find((filterSet:any) => filterSet.identifier === 'stage')

  if (!stageFilterSet) {
    return stages; // Frühzeitige Rückgabe, wenn kein 'stage' FilterSet gefunden wird
  }

  let filtersToTransform = stageFilterSet.filter

  // Selected Stage Filter
  const selectedFilters = stageFilterSet.filter.filter((filter:any) => filter.selected);

  if (selectedFilters.length > 0) {
    filtersToTransform = selectedFilters;
  }

  filtersToTransform.forEach((filter:any) => {
    const stage: Stage = {
      id: filter.identifier,
      title: filter.label
    };
    stages.push(stage);
  });

  return stages
}

export function transformFilterSetToCurrentDate(filterSets: any): any {
  const currentDate: any = null;
  const daysFilterSet = filterSets.find((filterSet:any) => filterSet.identifier === 'day')
  if (!daysFilterSet) {
    return currentDate; // Frühzeitige Rückgabe, wenn kein 'stage' FilterSet gefunden wird
  }

  // Selected Day Filter
  const selectedFilters = daysFilterSet.filter.filter((filter:any) => filter.selected);

  return selectedFilters
}

export function filterResourcesByEvents(
  events:any,
  resources: any
) {
  // Zuerst sammeln wir alle einzigartigen 'resourceId' Werte aus dem 'events' Array.
  // Ein Set ist hier effizient, da es Duplikate automatisch eliminiert.
  const uniqueResourceIds = new Set<string>();
  for (const event of events) {
    uniqueResourceIds.add(event.resourceId);
  }

  // Dann filtern wir das 'resources' Array.
  // Ein Element wird nur beibehalten, wenn seine 'id' im Set der 'uniqueResourceIds' enthalten ist.
  const filteredResources = resources.filter((resource:any) =>
    uniqueResourceIds.has(resource.id)
  );

  return filteredResources;
}

export function filterStagesByEvents(
  events:any,
  stages: any
) {
  const activeStageIds = new Set<string>();
  for (const event of events) {
    activeStageIds.add(event.resourceId);
  }

  return stages.map((stage:any) => ({
    ...stage,
    enabled: activeStageIds.has(stage.id)
  }));
}

export function filterEventsByRessource(
  events:Event[],
  resources: Stage[]
) {
  const resourceIds = new Set(resources.map(r => r.id));
  return events.filter(event => event.resourceId && resourceIds.has(event.resourceId));
}

export function calculateTimes (
  events:Event[]
) {
  if (!events || events.length === 0) {
    return { earliestStartInMinutes: 0, totalTimeInMinutes: 60*24 , latestEndInMinutes: 60*24 };
  }

  const startTimes = events.map(e => new Date(e.start));
  const endTimes = events.map(e => new Date(e.end));

  const earliestStart = new Date(Math.min(...startTimes.map(d => d.getTime())));
  const latestEnd = new Date(Math.max(...endTimes.map(d => d.getTime())));

  // Berechne Mitternacht (Start des Tages)
  const midnight = new Date(earliestStart);
  midnight.setHours(0, 0, 0, 0);

  // Zeitdifferenz seit Mitternacht in Minuten
  const rawStartMinutes = (earliestStart.getTime() - midnight.getTime()) / 60000;
  const rawEndMinutes = (latestEnd.getTime() - midnight.getTime()) / 60000;

  // Abrunden zur vollen Stunde
  const earliestStartInMinutes = (Math.floor(rawStartMinutes / 60) * 60);

  // Aufrunden zur vollen Stunde
  const latestEndInMinutes = (Math.ceil(rawEndMinutes / 60) * 60);

  const totalTimeInMinutes = latestEndInMinutes - earliestStartInMinutes;

  return {
    earliestStartInMinutes,
    totalTimeInMinutes,
    latestEndInMinutes
  };
}

export function filterEventsByStartDate(events: Event[], datum: string | Date): Event[] {
  const zielDatum = new Date(datum);
  if (isNaN(zielDatum.getTime())) {
    throw new Error("Ungültiges Datum übergeben.");
  }

  const zielTag = zielDatum.getFullYear();
  const zielMonat = zielDatum.getMonth();
  const zielDatumTag = zielDatum.getDate();

  return events.filter(event => {
    const start = new Date(event.start);
    return (
      start.getFullYear() === zielTag &&
      start.getMonth() === zielMonat &&
      start.getDate() === zielDatumTag
    );
  });
}

export function filterEventsByTimeRange(events: Event[], range: TimeRange): Event[] {
  const minTime = new Date(range.min.replace(' ', 'T'));
  const maxTime = new Date(range.max.replace(' ', 'T'));

  return events.filter(event => {
    const eventStart = new Date(event.start.replace(' ', 'T'));
    const eventEnd = new Date(event.end.replace(' ', 'T'));

    // Event beginnt oder endet innerhalb des Zeitraums
    return (
      (eventStart >= minTime && eventStart <= maxTime) ||
      (eventEnd >= minTime && eventEnd <= maxTime) ||
      (eventStart <= minTime && eventEnd >= maxTime) // Event umspannt gesamten Zeitraum
    );
  });
}

export function findCurrentDayInDayRange(spans: Day[], now: Date = new Date()): any {
  const nowDateStr = now.toISOString().split('T')[0];

  for (const span of spans) {
    const minDateStr = safeDateString(span.min);
    const maxDateStr = safeDateString(span.max);

    if (!minDateStr || !maxDateStr) {
      console.warn(`Ungültiges Datum in Zeitspanne: ${JSON.stringify(span)}`);
      continue;
    }

    if (nowDateStr >= minDateStr && nowDateStr <= maxDateStr) {
      // return minDateStr;
      return span;
    }
  }

  return undefined;
}

function safeDateString(dateStr: string): string | null {
  const isoString = dateStr.replace(' ', 'T');
  const date = new Date(isoString);
  return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
}

export function getMinAndMaxDay(spans: Day[]): { start: string; end: string } | undefined {
  if (spans.length === 0) {
    return undefined;
  }

  const minDates = spans
    .map(span => parseSafeDate(span.min))
    .filter((d): d is Date => d !== null);

  const maxDates = spans
    .map(span => parseSafeDate(span.max))
    .filter((d): d is Date => d !== null);

  if (minDates.length === 0 || maxDates.length === 0) {
    return undefined;
  }

  const earliest = minDates.reduce((a, b) => (a < b ? a : b));
  const latest = maxDates.reduce((a, b) => (a > b ? a : b));

  return {
    start: earliest.toISOString().split('T')[0],
    end: latest.toISOString().split('T')[0]
  };
}

function parseSafeDate(dateStr: string): Date | null {
  const isoStr = dateStr.replace(' ', 'T');
  const date = new Date(isoStr);
  return isNaN(date.getTime()) ? null : date;
}